import { buildSrpPath, parseSrpPath } from '@/src/lib/seo/srpPath';

/**
 * Promote-only canonical SRP URL: the path with its slug segment swapped to the
 * BE's `resolvedSlug` (or dropped, for the all-rentals slug, whose page is the
 * bare area URL), keeping query + page + locale verbatim — or `null` when
 * already on the resolved slug.
 *
 * No param stripping: redundant params (e.g. `?pets=cats-dogs` on
 * `/pet-friendly-…`) are kept, like legacy. That keeps the client morph a single
 * navigation and means the server never issues a strip-redirect mid-nav (which
 * is what blanked the page). Pure, so the server route (load-time redirect) and
 * the client hook (`useSrpSlugCanonicalization`) share it.
 */
export function computeCanonicalSrpUrl(args: {
  pathname: string;
  search: string;
  locales: ReadonlyArray<string>;
  resolvedSlug: string | undefined;
  allRentalsSlug: string;
}): string | null {
  const { pathname, search, locales, resolvedSlug, allRentalsSlug } = args;
  if (!resolvedSlug) return null;

  // Near-me URLs have no area segment; they own their canonical and never morph here.
  const srpPath = parseSrpPath(pathname, allRentalsSlug, locales);
  if (!srpPath?.resourceId || srpPath.slug === resolvedSlug) return null;

  const localePrefix = srpPath.locale ? `/${srpPath.locale}` : '';
  const targetPath = `${localePrefix}${buildSrpPath(srpPath.resourceId, resolvedSlug, srpPath.page ?? 1)}`;
  if (targetPath === pathname) return null;

  const normalizedSearch = search && !search.startsWith('?') ? `?${search}` : search;
  return `${targetPath}${normalizedSearch}`;
}
